import React from 'react';
import Mapir from "mapir-react-component";
import { MapPin, Navigation } from "lucide-react";
import locationIcon from "../assets/icons/Location.svg";
import 'mapir-react-component/dist/index.css';
import 'mapbox-gl/dist/mapbox-gl.css';

interface MapComponentProps {
  mapUrl?: string;
  latitude?: number;
  longitude?: number;
  name?: string;
  address?: string;
}

const MapComponent: React.FC<MapComponentProps> = ({ mapUrl, latitude, longitude, name, address }) => {
  const hasCoordinates = latitude != null && longitude != null;
  const directionsUrl = hasCoordinates
    ? `https://www.google.com/maps/dir/?api=1&destination=${longitude},${latitude}`
    : undefined;

  // Initialize Map component
  const Map = Mapir.setToken({
    transformRequest: (url: string) => {
      return {
        url: url,
        headers: {
          'x-api-key': 'eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImp0aSI6IjIyMWQ4ZjM4MDgxNTA1YTMwNWNhZTY4YWNmMzcwNmRhYzU5YmI5YzllM2NlM2M1ZTFiNWZhY2FhZTc2MjdlMTQ5MjAyNWViODVlZDVkN2I5In0.eyJhdWQiOiIzMTYyNiIsImp0aSI6IjIyMWQ4ZjM4MDgxNTA1YTMwNWNhZTY4YWNmMzcwNmRhYzU5YmI5YzllM2NlM2M1ZTFiNWZhY2FhZTc2MjdlMTQ5MjAyNWViODVlZDVkN2I5IiwiaWF0IjoxNzQyNjI0NDM0LCJuYmYiOjE3NDI2MjQ0MzQsImV4cCI6MTc0NTIxNjQzNCwic3ViIjoiIiwic2NvcGVzIjpbImJhc2ljIl19.DjwfXfL1bo4S0um881R_eeKxPXnQQCntmZbv3E0mw-9pthimlAiQHDDc9k-UjR94d0ZA6y3mmKn5UFJ9O-isMC9kCsLz0jVsqHaZXHUBkY5ijHi8rytRs_EfW12lZBdZ35WQejYRB1umr865eObl-EkSpFB_F4JjqCPbeeoi6P-5g5W68nHQwe17fkIBhHi610vh6sI5bmWRkjhZaKbn5FGrkpbvBkuEsDUj2Toxcir4OR0UR1SCD5L7I69SsvvSke44JWrPk--NVFIPgb24sC5scqcIG8oGh-WYTEvLB9cT6XkZLfFOiG0eK1lYSeKZniLOB6v47MmKKKhwvW067A',
          'Mapir-SDK': 'reactjs'
        }
      };
    }
  });

  return (
    <section className="w-full rounded-xl bg-transparent px-0 py-4 tablet:bg-transparent tablet:px-0 tablet:py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-[1.09rem] font-myYekanFaNumExtraBold leading-[1.5] text-[#222222]">نقشه</h2>
        {directionsUrl && (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`مسیریابی به ${name || "مکان"}`}
            className="hidden"
          >
            <Navigation size={16} aria-hidden="true" />
            <span>مسیریابی</span>
          </a>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">

        {hasCoordinates ? (
          <Mapir
            center={[longitude, latitude]}
            Map={Map}
            zoom={[15]}
            interactive={true}
            hash={false}
            trackResize={true}
            containerStyle={{
              width: '100%',
              height: '200px'
            }}
          >
            <Mapir.Marker
              coordinates={[longitude, latitude]}
              anchor="center"
              Image={locationIcon}
            />
            <Mapir.Popup
              coordinates={[longitude, latitude]}
              anchor="top"
              offset={[0, 30]}
            >
              <div className="text-[0.77rem] leading-[1.5] text-gray-800 font-myYekanMedium">
                {name}
              </div>
            </Mapir.Popup>
          </Mapir>
        ) : (
          <div className="py-8 text-center text-[0.77rem] leading-[1.8] text-gray-500 font-myYekanRegular">
            اطلاعات موقعیت مکانی موجود نیست
          </div>
        )}
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-3 py-3 desktop:gap-6 desktop:px-5 desktop:py-4">
          <div className="flex min-w-0 items-start gap-1.5 text-right">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#038545] desktop:h-6 desktop:w-6" aria-hidden="true" />
            <div className="min-w-0">
              <p className="truncate text-s font-myYekanDemibold text-gray-900">{address || name || "موقعیت مکان"}</p>
              {address && <p className="mt-1 truncate text-xs text-gray-500">{name}</p>}
            </div>
          </div>
          {directionsUrl && (
            <a href={directionsUrl} target="_blank" rel="noreferrer" className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-full bg-[#111313] px-4 text-xs font-myYekanDemibold text-white transition-colors hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545] focus-visible:ring-offset-2 desktop:h-12 desktop:gap-2 desktop:px-7 desktop:text-sm">
              <Navigation size={20} aria-hidden="true" />
              <span>مسیریابی</span>
            </a>
          )}
        </div>
      </div>

    </section>
  );
};

export default MapComponent;
